import {
  ApiResponse,
  MfaDetails,
  OfferingQueryParams,
  OfferingTransaction,
  PropertyRentContract,
  PropertyRentContractRequest,
  TotalAvailableSharesResponse,
  TradesQueryFilters,
  User,
  UserEmailChangeRequest,
  UserId,
  UserQueryParams,
  UserRequest,
} from '@arrived/models';

import { api } from './api';
import { getQueryStringFromFilters, unwrapApiResponse } from './utils';

export const getAllUsers = async (query?: UserQueryParams) => {
  const queryParameters = query ? getQueryStringFromFilters<OfferingQueryParams>(query) : '';

  const response = await api.get<ApiResponse<User[]>>(`/users/all${queryParameters}`);

  return unwrapApiResponse(response.data);
};

export const getCurrentUser = async () => {
  const response = await api.get<ApiResponse<User>>('/users');

  return unwrapApiResponse(response.data);
};

export const getUserMFADetails = async (userId: UserId) => {
  const response = await api.get<ApiResponse<MfaDetails>>(`/users/${userId}/mfa-details`);

  return unwrapApiResponse(response.data);
};

export const postUserResetMFA = async (userId: UserId) => {
  const response = await api.post<ApiResponse<MfaDetails>>(`/users/${userId}/reset-mfa`);

  return unwrapApiResponse(response.data);
};

export const postUserEnrollMFA = async (userId: UserId) => {
  const response = await api.post<ApiResponse<MfaDetails>>(`/users/${userId}/send-mfa-enrollment-email`);

  return unwrapApiResponse(response.data);
};

export const getUserById = async (userId: UserId) => {
  const response = await api.get<ApiResponse<User>>(`/users/${userId}`);

  return unwrapApiResponse(response.data);
};

export const postUser = async (userRequest: UserRequest) => {
  const response = await api.post<ApiResponse<User>>('/users', userRequest);

  return unwrapApiResponse(response.data);
};

export const patchUser = async (userRequest: UserRequest) => {
  const response = await api.patch<ApiResponse<User>>(`/users/${userRequest.id}`, userRequest);

  return unwrapApiResponse(response.data);
};

export const postChangeEmail = async (userId: UserId, userRequest: UserEmailChangeRequest) => {
  const response = await api.post<ApiResponse<string>>(`/users/${userId}/change-email`, userRequest);

  return unwrapApiResponse(response.data);
};

export const postChangePassword = async (userId: UserId) => {
  const response = await api.post<ApiResponse<string>>(`/users/${userId}/change-password`);

  return unwrapApiResponse(response.data);
};

export const getSharesAvailableForUser = async (userId: UserId, offeringId: number) => {
  const response = await api.get<ApiResponse<TotalAvailableSharesResponse>>(
    `/users/${userId}/offerings/${offeringId}/shares/available`
  );

  return unwrapApiResponse(response.data);
};

export const getUsersTrades = async (userId: UserId, filters?: TradesQueryFilters) => {
  const queryParameters = filters ? getQueryStringFromFilters<TradesQueryFilters>(filters) : '';

  const response = await api.get<ApiResponse<OfferingTransaction[]>>(`/users/${userId}/trades${queryParameters}`);

  return unwrapApiResponse(response.data);
};

export const deleteUser = async (userId: UserId) => {
  const response = await api.delete<ApiResponse<User>>(`/users/${userId}`);

  return unwrapApiResponse(response.data);
};

export const getUsersCurrentRentContract = async (userId: UserId) => {
  const response = await api.get<ApiResponse<PropertyRentContract>>(`/users/${userId}/rent/contracts/current`);

  return unwrapApiResponse(response.data);
};

export const getUsersRentContracts = async (userId: UserId) => {
  const response = await api.get<ApiResponse<PropertyRentContract>>(`/users/${userId}/rent/contracts`);

  return unwrapApiResponse(response.data);
};

export const postUsersRentContract = async (request: PropertyRentContractRequest) => {
  const response = await api.post<ApiResponse<PropertyRentContract>>(
    `/users/${request.userId}/rent/contracts`,
    request
  );

  return unwrapApiResponse(response.data);
};

export const deleteUsersRentContract = async (userId: UserId) => {
  const response = await api.delete<ApiResponse<PropertyRentContract>>(`/users/rent/contracts/${userId}`);

  return unwrapApiResponse(response.data);
};

export const getUserSsn = async (userId: UserId) => {
  const response = await api.get<ApiResponse<string>>(`/users/${userId}/ssn`);

  return unwrapApiResponse(response.data);
};
